// src/core/services/axiosClient.js
import axios from "axios";
import queryString from "query-string";
import { history, LocalStorageService } from "../../helpers";
import useConfigEnv from "../../zustand";

const localStorageService = LocalStorageService.getService();

console.log("Update Version 2.0");

// ====== DEFAULT HEADER GIỮ NGUYÊN (nếu cần dùng lại thì mở) ======
const defaultHeader = {
  // "Access-Control-Allow-Origin": "*",
  // "Content-Type": "application/json",
  // Accept: "application/json",
};

// ==== HÀNG ĐỢI LÚC REFRESH TOKEN ====
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// ==== CHỜ ENV LOAD XONG TRƯỚC KHI REQUEST ====
// Nếu bạn đã có waitUntilLoaded() sẵn trong store thì xài trực tiếp.
// Ở đây mình viết utility subscribe chờ env có apiEndPoint.
function waitForEnvReady() {
  const state = useConfigEnv.getState();
  if (state?.env?.apiEndPoint) return Promise.resolve(state.env);

  return new Promise((resolve) => {
    const unsubscribe = useConfigEnv.subscribe((newState) => {
      if (newState?.env?.apiEndPoint) {
        unsubscribe();
        resolve(newState.env);
      }
    });
  });
}

// ====== KHỞI TẠO CLIENT ======
const axiosClient = axios.create({
  // KHÔNG set baseURL cố định nữa; sẽ gắn động trong request interceptor
  headers: defaultHeader,
  paramsSerializer: (params) => queryString.stringify(params),
});
axios.defaults.timeout = 240000;

// ====== REQUEST INTERCEPTOR ======
axiosClient.interceptors.request.use(
  async (config) => {
    // Đợi env sẵn sàng, rồi gắn baseURL động
    const env = await waitForEnvReady();
    if (!config.baseURL) {
      config.baseURL = env.apiEndPoint; // ví dụ: https://api.school.example.com
    }

    // Gắn Authorization nếu có token
    const token = localStorageService.getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = "Bearer " + token;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ====== RESPONSE INTERCEPTOR ======
axiosClient.interceptors.response.use(
  (response) => handleResponse(response),
  async (error) => {
    const originalRequest = error.config;

    const handleError = (err) => {
      if (!err?.response) return err;
      const { data } = err.response;
      if (data && data.code === 400 && ["invalid_grant"].includes(data?.data?.error)) {
        clearAuthToken();
      }
      return data;
    };

    // Nếu không có response (network error, CORS, v.v.) -> trả về sớm
    if (!error?.response) {
      return Promise.reject(handleError(error));
    }

    const status = error.response.status;
    const code = error.response?.data?.code;

    const isAuthProblem =
      (status === 403 || status === 401 || status === 404) &&
      (["TOKEN_EXPIRED", "AUTHEN_FAIL", 401].includes(code));

    if (!isAuthProblem) {
      return Promise.reject(handleError(error));
    }

    // Nếu đã có request khác đang refresh -> vào hàng đợi
    if (isRefreshing) {
      try {
        const newToken = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers["Authorization"] = "Bearer " + newToken;
        return axiosClient.request(originalRequest);
      } catch (e) {
        return Promise.reject(e);
      }
    }

    // Bắt đầu refresh
    isRefreshing = true;

    try {
      const env = await waitForEnvReady();
      const refreshToken = localStorageService.getRefreshToken();
      const accessTokenExp = localStorageService.getAccessToken();

      if (!refreshToken) {
        clearAuthToken();
        return Promise.reject(handleError(error));
      }

      // Ưu tiên client_id/secret từ env.json; fallback về process.env nếu thiếu
      const CLIENT_ID = env.client_id || process.env.REACT_APP_CLIENT_ID;
      const CLIENT_SECRET = env.client_secret || process.env.REACT_APP_CLIENT_SECRET;

      // Dùng application/x-www-form-urlencoded cho đúng chuẩn OAuth2
      const body = new URLSearchParams({
        refresh_token: refreshToken,
        grant_type: "refresh_token",
        client_id: CLIENT_ID || "",
        client_secret: CLIENT_SECRET || "",
      });

      const tokenRes = await axios.post(
        `${env.apiEndPoint}/authentication/oauth2/token`,
        body.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${accessTokenExp || ""}`,
          },
        }
      );

      const data = tokenRes.data;

      // 1) Lưu token
      localStorageService.setToken(data);
      localStorageService.setRefreshToken(data);

      // 2) Update header Authorization cho axios & original request
      axios.defaults.headers.common["Authorization"] = "Bearer " + data.access_token;
      originalRequest.headers["Authorization"] = "Bearer " + data.access_token;

      // Thông báo cho các request đang chờ
      processQueue(null, data.access_token);

      // 3) Retry request cũ
      return axiosClient.request(originalRequest);
    } catch (err) {
      // Các case refresh fail -> clear token & chuyển login
      const error_code = err?.response?.data?.error_code;
      const isInvalidGrant =
        (err?.response?.status === 400 || err?.response?.status === 401) &&
        ["invalid_grant"].includes(err?.response?.data?.error);

      if (
        err?.error_code === "AUTHEN_FAIL" ||
        err?.error_code === "TOKEN_EXPIRED" ||
        err?.error_code === "TOKEN_INCORRECT" ||
        err?.code === "ERR_BAD_REQUEST" ||
        error_code === "AUTHEN_FAIL" ||
        isInvalidGrant
      ) {
        clearAuthToken();
      }

      processQueue(err, null);
      return Promise.reject(handleError(err));
    } finally {
      isRefreshing = false;
    }
  }
);

// ====== HELPERS ======
const handleResponse = (res) => {
  if (res && res.data) return res.data;
  return res;
};

const clearAuthToken = () => {
  localStorageService.clearToken();
  history.push(`/login`);
};

export default axiosClient;
